import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, List, Typography, VerticalSpacing } from '@banhala/ui-kit';
import { GoodsInfoWebView } from './components/GoodsInfoWebView';
import { ContentContainer } from '../ContentContainer';
import { GoodsAtomicRenderer } from './components/AtomicRenderer';
import { RelatedOutfits } from './components/RelatedOutfits';
import { RelatedTags } from './components/RelatedTags';
import { LegalInfo } from './components/LegalInfo';
import { useElementViewportTracking } from '../../../../store/sectionVisibility';

export const GoodsInfoTab: React.FC<{
  focused: boolean;
  onChangeTabHeight: (height: number) => void;
}> = props => {
  const onChangeTabHeightRef = useRef(props.onChangeTabHeight);
  onChangeTabHeightRef.current = props.onChangeTabHeight;
  const trackingRef = useElementViewportTracking('atomic', 0);

  const [height, setHeight] = useState({
    info: 0,
    atomic: 0,
    legal: 0,
    codi: 0,
    tags: 0,
  });

  useEffect(() => {
    const nextHeight = Object.values<number>(height).reduce(
      (prev, acc) => prev + acc,
      0,
    );
    onChangeTabHeightRef.current(nextHeight);
  }, [height]);

  const handleInfoHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.info === h) return prev;
      return { ...prev, info: h };
    });
  }, []);

  const handleAtomicHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.atomic === h) return prev;
      return { ...prev, atomic: h };
    });
  }, []);

  const handleCodiHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.codi === h) return prev;
      return { ...prev, codi: h };
    });
  }, []);

  const handleTagsHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.tags === h) return prev;
      return { ...prev, tags: h };
    });
  }, []);

  const handleLegalHeight = useCallback((h: number) => {
    setHeight(prev => {
      if (prev.legal === h) return prev;
      return { ...prev, legal: h };
    });
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: string }) => {
      if (item === 'info') {
        return <GoodsInfoWebView onChangeHeight={handleInfoHeight} />;
      }

      if (item === 'atomic') {
        return (
          <Flex ref={trackingRef}>
            <GoodsAtomicRenderer
              screen={'GOODS_DETAIL'}
              focused={props.focused}
              onChangeHeight={handleAtomicHeight}
            />
          </Flex>
        );
      }

      if (item === 'codi') {
        return (
          <RelatedOutfits
            focused={props.focused}
            onChangeHeight={handleCodiHeight}
          />
        );
      }

      if (item === 'tags') {
        return <RelatedTags onChangeHeight={handleTagsHeight} />;
      }

      if (item === 'legal') {
        return <LegalInfo onChangeHeight={handleLegalHeight} />;
      }

      return (
        <Flex.Center height={300}>
          <Typography type={'subtitle2'}>{item}</Typography>
        </Flex.Center>
      );
    },
    [
      props.focused,
      trackingRef,
      handleInfoHeight,
      handleAtomicHeight,
      handleCodiHeight,
      handleTagsHeight,
      handleLegalHeight,
    ],
  );

  return (
    <ContentContainer focused={props.focused}>
      <List
        data={['info', 'atomic', 'codi', 'tags', 'legal']}
        keyExtractor={(item, index) => `${item}-${index}`.toString()}
        ListFooterComponent={<VerticalSpacing size={36} />}
        renderItem={renderItem}
      />
    </ContentContainer>
  );
};
